import {
	useWhatsAppTemplateArchive,
	useWhatsAppTemplateIndex,
	useWhatsAppTemplateStore,
	useWhatsAppTemplateSync,
	useWhatsAppTemplateUnarchive,
	useWhatsAppTemplateUpdate,
	useWhatsAppTemplateUpdateParameterMap,
	whatsAppTemplateIndexQueryKey,
} from '@jobilla/nexus-sdk'
import type {
	UpdateWhatsAppTemplateParameterMapData,
	WhatsAppTemplateData,
	WhatsAppTemplateIndex403,
	WhatsAppTemplateStoreMutationRequest,
	WhatsAppTemplateUpdateRequest,
} from '@jobilla/nexus-sdk'
import { useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'

export type UseWhatsAppTemplatesReturn = {
	templates: WhatsAppTemplateData[]
	isLoading: boolean
	error: WhatsAppTemplateIndex403 | null
	syncTemplates: () => Promise<void>
	isSyncing: boolean
	createTemplate: (data: WhatsAppTemplateStoreMutationRequest) => Promise<void>
	isCreating: boolean
	updateParameterMap: (templateId: string, data: UpdateWhatsAppTemplateParameterMapData) => Promise<void>
	isUpdatingParameterMap: boolean
	editTemplate: (templateId: string, data: WhatsAppTemplateUpdateRequest) => Promise<void>
	isEditing: boolean
	archiveTemplate: (templateId: string) => Promise<void>
	isArchiving: boolean
	unarchiveTemplate: (templateId: string) => Promise<void>
	isUnarchiving: boolean
}

export function useWhatsAppTemplates(
	organisationId: string,
	options?: { enabled?: boolean; includeArchived?: boolean },
): UseWhatsAppTemplatesReturn {
	const queryClient = useQueryClient()
	const enabled = (options?.enabled ?? true) && Boolean(organisationId)
	const includeArchived = options?.includeArchived ?? false

	// Invalidate the params-less prefix so both the active and archived lists refetch.
	const baseQueryKey = whatsAppTemplateIndexQueryKey(organisationId)
	const invalidate = () => queryClient.invalidateQueries({ queryKey: baseQueryKey })

	const templatesQuery = useWhatsAppTemplateIndex(organisationId, { includeArchived }, {
		enabled,
		refetchInterval: (query) => {
			if (!enabled) { return false }
			const data = query.state.data
			if (!data) { return false }
			const hasPending = data.some((t) => t.status === 'PENDING' || t.status === 'AWAITING_QUALITY_RATING')
			return hasPending ? 30_000 : false
		},
	})

	const syncMutation = useWhatsAppTemplateSync({
		mutationKey: ['whatsapp-template-sync', organisationId],
		onSuccess: () => {
			void invalidate()
		},
	})

	const createMutation = useWhatsAppTemplateStore({
		mutationKey: ['whatsapp-template-create', organisationId],
		onSuccess: () => {
			void invalidate()
		},
	})

	const updateParameterMapMutation = useWhatsAppTemplateUpdateParameterMap({
		mutationKey: ['whatsapp-template-update-parameter-map', organisationId],
		onSuccess: () => {
			void invalidate()
		},
	})

	const editMutation = useWhatsAppTemplateUpdate({
		mutationKey: ['whatsapp-template-edit', organisationId],
		onSuccess: () => {
			void invalidate()
		},
	})

	const archiveMutation = useWhatsAppTemplateArchive({
		mutationKey: ['whatsapp-template-archive', organisationId],
		onSuccess: () => {
			void invalidate()
		},
	})

	const unarchiveMutation = useWhatsAppTemplateUnarchive({
		mutationKey: ['whatsapp-template-unarchive', organisationId],
		onSuccess: () => {
			void invalidate()
		},
	})

	const syncTemplates = useCallback(async () => {
		await syncMutation.mutateAsync({ organisationId })
	}, [syncMutation, organisationId])

	const createTemplate = useCallback(async (data: WhatsAppTemplateStoreMutationRequest) => {
		await createMutation.mutateAsync({ organisationId, data })
	}, [createMutation, organisationId])

	const updateParameterMap = useCallback(
		async (templateId: string, data: UpdateWhatsAppTemplateParameterMapData) => {
			await updateParameterMapMutation.mutateAsync({ organisationId, templateId, data })
		},
		[updateParameterMapMutation, organisationId],
	)

	const editTemplate = useCallback(
		async (templateId: string, data: WhatsAppTemplateUpdateRequest) => {
			await editMutation.mutateAsync({ organisationId, templateId, data })
		},
		[editMutation, organisationId],
	)

	const archiveTemplate = useCallback(async (templateId: string) => {
		await archiveMutation.mutateAsync({ organisationId, templateId })
	}, [archiveMutation, organisationId])

	const unarchiveTemplate = useCallback(async (templateId: string) => {
		await unarchiveMutation.mutateAsync({ organisationId, templateId })
	}, [unarchiveMutation, organisationId])

	return {
		templates: templatesQuery.data ?? [],
		isLoading: templatesQuery.isLoading,
		error: templatesQuery.error,
		syncTemplates,
		isSyncing: syncMutation.isPending,
		createTemplate,
		isCreating: createMutation.isPending,
		updateParameterMap,
		isUpdatingParameterMap: updateParameterMapMutation.isPending,
		editTemplate,
		isEditing: editMutation.isPending,
		archiveTemplate,
		isArchiving: archiveMutation.isPending,
		unarchiveTemplate,
		isUnarchiving: unarchiveMutation.isPending,
	}
}
